import { LoadingButton } from "@mui/lab";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useErrorHandler } from "@sinch/hooks/useErrorLogger";
import { useFormMutationSubmit } from "@sinch/hooks/useFormMutationSubmit";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { FC, Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ComponentCode } from "@sinch/components/application/RateComponent/CreateComponent/ComponentCode";
import { ComponentName } from "@sinch/components/application/RateComponent/CreateComponent/ComponentName";
import { CreateComponentArchivedField } from "@sinch/components/application/RateComponent/CreateComponent/CreateIntervalArchivedField";
import { CreateComponentDescriptionField } from "@sinch/components/application/RateComponent/CreateComponent/CreateIntervalDescriptionField";
import type { AddRateComponentFormFields } from "@sinch/components/application/RateComponent/formSchema";
import { addRateComponentSchema } from "@sinch/components/application/RateComponent/formSchema";
import type {
  AddRateComponentMutationVariables,
  EditRateComponentMutation,
  EditRateComponentMutationVariables,
} from "@sinch/components/application/RateComponent/queries.types";
import { AddRateComponentMutation } from "@sinch/components/application/RateComponent/queries.types";
import { useRateComponent } from "@sinch/components/application/RateComponent/UseRateComponent";
import { CollapsibleListCard } from "@sinch/components/CollapsibleListCard/CollapsibleListCard";
import { DialogCloseIcon } from "@sinch/components/DialogCloseIcon/DialogCloseIcon";
import { FormContainer } from "@sinch/components/form/FormContainer/FormContainer";
import { HandleErrorBox } from "@sinch/components/HandleErrorBox/HandleErrorBox";

export interface RateComponentFormDialogProps {
  componentId?: string;
  onCancel: () => void;
  open: boolean;
  onConfirm: (rateComponent?: AddRateComponentMutation["createRateComponent"]["payload"] | null) => void;
  defaults?: Partial<AddRateComponentFormFields>;
}

export const RateComponentFormDialog: FC<RateComponentFormDialogProps> = (props) => (
  <Dialog fullWidth maxWidth="xs" onClose={props.onCancel} open={props.open}>
    <Suspense
      fallback={
        <>
          <DialogTitle>
            <DialogCloseIcon onClick={props.onCancel} />
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ pt: 3 }}>
              <Skeleton height={40} variant="rounded" />
              <span>
                <Skeleton height={20} variant="text" width={200} />
                <Skeleton height={40} variant="rounded" />
              </span>
              <span>
                <Skeleton height={20} variant="text" width={200} />
                <Skeleton height={40} variant="rounded" />
              </span>
            </Stack>
          </DialogContent>
        </>
      }
    >
      <RateComponentForm {...props} />
    </Suspense>
  </Dialog>
);

const RateComponentForm: FC<RateComponentFormDialogProps> = ({ componentId, onCancel, defaults, onConfirm }) => {
  const { t } = useTranslation<any>();
  const isEditMode = isNotNil(componentId);
  const { create, edit, loading, rateComponent } = useRateComponent({
    id: componentId,
    queryOptions: { fetchPolicy: "network-only" },
  });
  const { catchErr, errorId, clearErr } = useErrorHandler();

  const defaultData = useMemo<AddRateComponentFormFields>(() => {
    const currentComponent = rateComponent?.rateComponent;
    if (currentComponent) {
      return {
        name: currentComponent.name,
        code: currentComponent.code,
        archived: currentComponent.archived,
        description: currentComponent.description ?? "",
      };
    }
    return { ...addRateComponentSchema.getDefault(), ...defaults };
  }, [rateComponent, defaults]);

  const form = useMuiForm<AddRateComponentFormFields>({
    schema: addRateComponentSchema,
    formOptions: { defaultValues: defaultData },
  });

  const sendCreate = useFormMutationSubmit<
    AddRateComponentMutation,
    AddRateComponentMutationVariables,
    AddRateComponentFormFields
  >({
    mutate: create,
    form,
    variableMapper: (fields) => ({ input: fields }),
  });

  const sendEdit = useFormMutationSubmit<
    EditRateComponentMutation,
    EditRateComponentMutationVariables,
    AddRateComponentFormFields
  >({
    mutate: edit,
    form,
    variableMapper: (fields) => ({ id: componentId!, input: fields }),
  });

  const handleMutationSuccess = (payload?: AddRateComponentMutation["createRateComponent"]["payload"] | null) => {
    form.reset();
    onConfirm(payload);
  };

  const submitForm = async () => {
    await catchErr(async () => {
      clearErr();
      if (isEditMode) {
        const response = await sendEdit();
        if (response?.editRateComponent?.result) {
          handleMutationSuccess(); // No payload for onConfirm in edit mode
        }
      } else {
        const response = await sendCreate();
        if (response?.createRateComponent?.result) {
          handleMutationSuccess(response?.createRateComponent?.payload);
        }
      }
    });
  };

  const usedInRatelists = rateComponent?.rateComponent?.ratelists ?? [];

  return (
    <>
      <DialogTitle>
        {isEditMode ? t("RateBuilder.editRateComponent") : t("RateBuilder.addRateComponent")}
        <DialogCloseIcon onClick={onCancel} />
      </DialogTitle>
      <DialogContent>
        {errorId && <HandleErrorBox errorId={errorId} />}
        <FormContainer form={form}>
          <Stack spacing={3}>
            {isEditMode && <CreateComponentArchivedField />}
            <ComponentName />
            <ComponentCode />
            <CreateComponentDescriptionField />
            {isNotNilOrEmpty(usedInRatelists) && (
              <CollapsibleListCard
                items={usedInRatelists.map((rl) => rl.name) ?? []}
                summaryText={t("RateUnit.useInRatelistsCount", { count: usedInRatelists?.length ?? 0 })}
              />
            )}
          </Stack>
        </FormContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} size="large">
          {t("cancel")}
        </Button>
        <LoadingButton loading={loading} onClick={submitForm} size="large" variant="contained">
          {t("confirm")}
        </LoadingButton>
      </DialogActions>
    </>
  );
};
